import { defineComponent, onMounted, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/role';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filterListToOptions, IdProps, sequence, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useRoleForm } from './useRoleForm';
import { useRoleRecord } from './useRoleRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';
import { RoleRecord } from '@/entities/role';
import { Events } from '@/ui/events';
import { FeatureNames } from '@/ui/components/NewFeature';
import { EventBus } from '@/ui/EventBus';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const RoleEditPage = defineComponent<IdProps>({
  name: 'RoleEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('roles')) {
      return renderResourceTeaserPage(t('common.roles'), t('role.teaser'), t('common.teaser'), 'roleIndex');
    }

    if (!hasPermission(Permissions.MANAGE_PERMISSIONS)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const userOptions = ref<SelectOptions>([]);

    // create empty record
    const record = useRoleRecord();

    const roleDetail = ref<RoleRecord>();

    // get record
    const getRecord = useTask(
      () => sequence(get(props.id), getAllUsers()),
      ([{ data, version }, users]) => {
        roleDetail.value = data;
        userOptions.value = filterListToOptions(users);
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => toastAndRedirect(t('role.updated'), 'administration', { [queryTabName]: AdministrationTabNames.ROLE }),
    );

    // form
    const { formFields, handleSubmit, isLoading } = useRoleForm(record, sendRecord);

    useCreatableUserModal(userOptions, id => {
      record.value.users.push(id);
    });

    const onSubmit = () => handleSubmit(roleDetail.value);

    onMounted(() => EventBus.$emit(Events.viewedNewFeature, FeatureNames.ENABLE_ENTITY_ITEM_RESTRICTIONS));

    return function render() {
      return container(
        [
          pageTitle(t('role.edit')),
          box([loader('form', getRecord, [
            ...formFields(userOptions.value),
            loadingButton(
              t('role.update'),
              onSubmit,
              isLoading.value,
            ),
          ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
